// src/pages/PrivacyPage.tsx — Slice D commit 6.
//
// Route: /me/privacy. Data-sharing statement + self-delete with reauth.
// Ported from docs/prototype/apps2.jsx lines 587-620 with two divergences:
//
//   - mailto target is privacy@nymr.co.uk (STATUS §3 V1 decision — the
//     data-export "Download my data" button is deferred to V2; for now
//     the corporate privacy mailbox handles all SAR + erasure requests).
//
//   - Delete confirmation requires a reauth body, not just a Yes/Cancel
//     button. The user types their email back (matched case-insensitive
//     to me.upn), and PIN sessions also enter their current PIN on the
//     real PinDots + NumPad. SSO sessions skip the PIN — being signed in
//     via SSO is the reauth. DELETE /api/me enforces this server-side.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, apiFetch } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import {
  Button,
  Card,
  Eyebrow,
  Field,
  Icons,
  NumPad,
  PinDots,
  PIN_LENGTH,
  PIN_REGEX,
  TopBar,
} from "../components/ui";

export function PrivacyPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [pinBuffer, setPinBuffer] = useState("");
  const [pinShake, setPinShake] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [lastAdminError, setLastAdminError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (!me) {
    return (
      <div>
        <TopBar title="Privacy & data" onBack={() => navigate("/me")} />
      </div>
    );
  }

  const emailMatches =
    confirmationEmail.trim().toLowerCase() === me.upn.toLowerCase();
  const pinReady = me.authMethod === "sso"
    || (pinBuffer.length === PIN_LENGTH && PIN_REGEX.test(pinBuffer));
  const canDelete = emailMatches && pinReady && !deleting;

  function triggerPinShake() {
    setPinShake(true);
    window.setTimeout(() => setPinShake(false), 420);
  }

  function resetConfirm() {
    setShowConfirm(false);
    setConfirmationEmail("");
    setPinBuffer("");
    setEmailError(null);
    setPinError(null);
    setLastAdminError(null);
    setGenericError(null);
  }

  async function confirmDelete() {
    setEmailError(null);
    setPinError(null);
    setLastAdminError(null);
    setGenericError(null);

    if (!emailMatches) {
      setEmailError("Email doesn't match the account on file.");
      return;
    }

    setDeleting(true);
    try {
      const result = await apiFetch<{
        ok: true;
        deleted: true;
        ssoLogoutUrl: string | null;
      }>("/api/me", {
        method: "DELETE",
        body: JSON.stringify({
          confirmationEmail,
          ...(me?.authMethod === "pin" ? { currentPin: pinBuffer } : {}),
        }),
      });

      // Take the server's word on the post-delete redirect. PIN gets
      // /login, SSO gets /.auth/logout to wind down the OAuth session.
      if (result.ssoLogoutUrl) {
        window.location.href = result.ssoLogoutUrl;
      } else {
        navigate("/login", { replace: true });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401 && /pin/i.test(e.message)) {
          setPinError("Current PIN incorrect.");
          triggerPinShake();
          setPinBuffer("");
        } else if (e.status === 401) {
          setEmailError(e.message);
        } else if (e.status === 409) {
          setLastAdminError(e.message);
        } else {
          setGenericError(e.message);
        }
      } else {
        setGenericError("Delete failed. Try again.");
      }
      setDeleting(false);
    }
  }

  return (
    <div>
      <TopBar title="Privacy & data" onBack={() => navigate("/me")} />
      <div
        style={{
          padding: "14px 16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <Card>
          <Eyebrow>Data sharing statement</Eyebrow>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <p
              style={{
                fontFamily: F.body,
                fontSize: 15,
                color: C.inkSoft,
                fontWeight: 500,
                lineHeight: 1.65,
                margin: 0,
              }}
            >
              North Yorkshire Moors Railway ("NYMR") processes your personal data in
              accordance with UK GDPR and the Data Protection Act 2018, as set out
              in the privacy notice you acknowledged when you joined NYMR as a
              staff member or volunteer.
            </p>
            <p
              style={{
                fontFamily: F.body,
                fontSize: 15,
                color: C.inkSoft,
                fontWeight: 500,
                lineHeight: 1.65,
                margin: 0,
              }}
            >
              The NYMR Hub application stores your personal details, emergency
              contact information, department membership, and usage data solely
              for operational purposes. This data is confidential and must not be
              shared with anyone outside of NYMR.
            </p>
            <p
              style={{
                fontFamily: F.body,
                fontSize: 15,
                color: C.inkSoft,
                fontWeight: 500,
                lineHeight: 1.65,
                margin: 0,
              }}
            >
              You may request access to, correction of, or deletion of your
              personal data at any time by contacting{" "}
              <a
                href="mailto:privacy@nymr.co.uk"
                style={{ color: C.brassDk, fontWeight: 700 }}
              >
                privacy@nymr.co.uk
              </a>
              .
            </p>
          </div>
        </Card>

        {lastAdminError && (
          <Card style={{ borderColor: "rgba(184,58,43,0.35)", background: "#FEF8F6" }}>
            <Eyebrow c={C.signalDk}>Cannot delete</Eyebrow>
            <div
              style={{
                fontFamily: F.body,
                fontSize: 14,
                fontWeight: 700,
                color: C.signalDk,
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              {lastAdminError}
            </div>
          </Card>
        )}

        {!showConfirm ? (
          <Button variant="danger" fullWidth onClick={() => setShowConfirm(true)}>
            <Icons.Close s={16} c={C.signalDk} />
            Delete my account
          </Button>
        ) : (
          <Card style={{ borderColor: "rgba(184,58,43,0.35)", background: "#FEF8F6" }}>
            <Eyebrow c={C.signalDk}>Are you sure?</Eyebrow>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 20,
                fontWeight: 700,
                color: C.signalDk,
                marginTop: 2,
                letterSpacing: -0.3,
              }}
            >
              This cannot be undone
            </div>
            <p
              style={{
                fontFamily: F.body,
                fontSize: 14,
                color: C.inkSoft,
                fontWeight: 600,
                lineHeight: 1.55,
                marginTop: 10,
              }}
            >
              This will permanently delete your account. All preferences, PINs,
              and settings will be lost. You will need to re-register. This does
              NOT remove you from the approved access list.
            </p>

            <div style={{ marginTop: 14 }}>
              <Field
                label="Type your email to confirm"
                value={confirmationEmail}
                onChange={(v) => {
                  setConfirmationEmail(v);
                  setEmailError(null);
                }}
                type="email"
                ph={me.upn}
              />
              <div
                style={{
                  marginTop: -8,
                  fontFamily: F.serif,
                  fontStyle: "italic",
                  fontSize: 12.5,
                  color: C.inkSoft,
                  fontWeight: 500,
                  marginBottom: 12,
                }}
              >
                Type your email address to confirm.
              </div>
              {emailError && (
                <div
                  style={{
                    marginTop: -4,
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: C.signalBg,
                    border: `1px solid rgba(184,58,43,0.22)`,
                    color: C.signalDk,
                    fontFamily: F.body,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {emailError}
                </div>
              )}
            </div>

            {me.authMethod === "pin" && (
              <div style={{ marginTop: 4 }}>
                <Eyebrow style={{ marginBottom: 8 }}>
                  Confirm with your current PIN
                </Eyebrow>
                <PinDots value={pinBuffer} shake={pinShake} />
                {pinError && (
                  <div
                    style={{
                      marginTop: -10,
                      marginBottom: 12,
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: C.signalBg,
                      border: `1px solid rgba(184,58,43,0.22)`,
                      color: C.signalDk,
                      fontFamily: F.body,
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {pinError}
                  </div>
                )}
                <NumPad
                  length={pinBuffer.length}
                  onPress={(char) => {
                    if (pinBuffer.length >= PIN_LENGTH) return;
                    setPinBuffer(pinBuffer + char);
                    setPinError(null);
                  }}
                  onDelete={() => {
                    if (pinBuffer.length === 0) return;
                    setPinBuffer(pinBuffer.slice(0, -1));
                    setPinError(null);
                  }}
                  onSubmit={confirmDelete}
                  disabled={deleting}
                />
              </div>
            )}

            {genericError && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: C.signalBg,
                  border: `1px solid rgba(184,58,43,0.22)`,
                  color: C.signalDk,
                  fontFamily: F.body,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {genericError}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <Button
                variant="primary"
                fullWidth
                disabled={!canDelete}
                onClick={confirmDelete}
                style={{
                  background: canDelete ? C.signalDk : C.signalBg,
                  color: canDelete ? "#FFFCEC" : C.signalDk,
                  boxShadow: canDelete
                    ? "0 2px 6px rgba(138,42,31,0.3)"
                    : "none",
                }}
              >
                {deleting ? "Deleting..." : "Yes, delete"}
              </Button>
              <Button variant="secondary" onClick={resetConfirm} disabled={deleting}>
                Cancel
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
